import { useCallback, useRef } from 'react';

/**
 * One IntersectionObserver, shared by every revealed element on the page.
 * Elements are unobserved after their first reveal — the animation plays once.
 */
let observer: IntersectionObserver | null = null;

function getObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') return null;

  observer ??= new IntersectionObserver(
    (entries, self) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) continue;
        (entry.target as HTMLElement).dataset.reveal = 'shown';
        self.unobserve(entry.target);
      }
    },
    { rootMargin: '0px 0px -12% 0px', threshold: 0.12 },
  );

  return observer;
}

/**
 * Returns a ref callback that fades its element in when it first scrolls into
 * view. Reduced-motion users get the final state immediately (see global.css).
 */
export function useReveal<T extends HTMLElement>() {
  const observed = useRef<T | null>(null);

  return useCallback((node: T | null) => {
    const instance = getObserver();

    if (observed.current && instance) instance.unobserve(observed.current);
    observed.current = node;

    if (!node) return;
    if (!instance) {
      node.dataset.reveal = 'shown'; // no observer support: show, never hide
      return;
    }
    instance.observe(node);
  }, []);
}
